import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DriverLeaderboard = () => {
  const [sortBy, setSortBy] = useState('safety');
  const [timeframe, setTimeframe] = useState('week');

  const drivers = [
    {
      id: 1,
      name: 'Michael Johnson',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      safetyScore: 98,
      efficiency: 94,
      onTimeDelivery: 96,
      fuelEfficiency: 92,
      totalMiles: 1247,
      incidents: 0,
      rank: 1,
      change: 2
    },
    {
      id: 2,
      name: 'Sarah Williams',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      safetyScore: 96,
      efficiency: 97,
      onTimeDelivery: 94,
      fuelEfficiency: 95,
      totalMiles: 1189,
      incidents: 1,
      rank: 2,
      change: -1
    },
    {
      id: 3,
      name: 'David Chen',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      safetyScore: 94,
      efficiency: 91,
      onTimeDelivery: 98,
      fuelEfficiency: 89,
      totalMiles: 1356,
      incidents: 0,
      rank: 3,
      change: 1
    },
    {
      id: 4,
      name: 'Lisa Rodriguez',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      safetyScore: 92,
      efficiency: 89,
      onTimeDelivery: 91,
      fuelEfficiency: 87,
      totalMiles: 1098,
      incidents: 2,
      rank: 4,
      change: 0
    },
    {
      id: 5,
      name: 'James Wilson',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      safetyScore: 89,
      efficiency: 86,
      onTimeDelivery: 88,
      fuelEfficiency: 84,
      totalMiles: 967,
      incidents: 1,
      rank: 5,
      change: -2
    }
  ];

  const getScoreColor = (score) => {
    if (score >= 95) return 'text-success';
    if (score >= 85) return 'text-warning';
    return 'text-error';
  };

  const getRankChange = (change) => {
    if (change > 0) return { icon: 'TrendingUp', color: 'text-success' };
    if (change < 0) return { icon: 'TrendingDown', color: 'text-error' };
    return { icon: 'Minus', color: 'text-muted-foreground' };
  };

  const getSortedDrivers = () => {
    return [...drivers]?.sort((a, b) => {
      switch (sortBy) {
        case 'safety': return b?.safetyScore - a?.safetyScore;
        case 'efficiency': return b?.efficiency - a?.efficiency;
        case 'onTime': return b?.onTimeDelivery - a?.onTimeDelivery;
        case 'fuel': return b?.fuelEfficiency - a?.fuelEfficiency;
        default: return a?.rank - b?.rank;
      }
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Driver Performance</h3>
          <div className="flex items-center space-x-2">
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e?.target?.value)}
              className="px-3 py-1 text-xs bg-background border border-border rounded-lg text-foreground"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>
          </div>
        </div>

        {/* Sort Options */}
        <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => setSortBy('safety')}
            className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
              sortBy === 'safety' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Safety
          </button>
          <button
            onClick={() => setSortBy('efficiency')}
            className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
              sortBy === 'efficiency' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Efficiency
          </button>
          <button
            onClick={() => setSortBy('onTime')}
            className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
              sortBy === 'onTime' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            On-Time
          </button>
        </div>
      </div>
      {/* Driver List */}
      <div className="max-h-96 overflow-y-auto">
        {getSortedDrivers()?.map((driver, index) => {
          const rankChange = getRankChange(driver?.change);
          const currentScore = sortBy === 'safety' ? driver?.safetyScore : 
                              sortBy === 'efficiency' ? driver?.efficiency :
                              sortBy === 'onTime' ? driver?.onTimeDelivery : driver?.fuelEfficiency;

          return (
            <div key={driver?.id} className="flex items-center justify-between p-4 border-b border-border last:border-b-0 hover:bg-muted nav-transition">
              <div className="flex items-center space-x-3">
                {/* Rank */}
                <div className="flex items-center justify-center w-8 h-8 bg-muted rounded-full">
                  <span className="text-sm font-bold text-foreground">#{index + 1}</span>
                </div>

                {/* Avatar & Info */}
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <Image
                      src={driver?.avatar}
                      alt={driver?.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    {driver?.incidents === 0 && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full flex items-center justify-center">
                        <Icon name="Check" size={10} color="white" />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground">{driver?.name}</h4>
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <span>{driver?.totalMiles?.toLocaleString()} miles</span>
                      <span>•</span>
                      <span>{driver?.incidents} incidents</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Score & Change */}
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className={`text-lg font-bold ${getScoreColor(currentScore)}`}>
                    {currentScore}%
                  </div>
                  <div className={`flex items-center space-x-1 ${rankChange?.color}`}>
                    <Icon name={rankChange?.icon} size={12} />
                    <span className="text-xs">{Math.abs(driver?.change)}</span>
                  </div>
                </div>

                {/* Performance Indicators */}
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-success rounded-full" title="Safety Score"></div>
                    <span className="text-xs text-muted-foreground">{driver?.safetyScore}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-accent rounded-full" title="Efficiency"></div>
                    <span className="text-xs text-muted-foreground">{driver?.efficiency}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Footer Stats */}
      <div className="p-4 bg-muted border-t border-border">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-foreground">
              {(drivers?.reduce((sum, d) => sum + d?.safetyScore, 0) / drivers?.length)?.toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground">Avg Safety</div>
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">
              {drivers?.reduce((sum, d) => sum + d?.incidents, 0)}
            </div>
            <div className="text-xs text-muted-foreground">Total Incidents</div>
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">
              {drivers?.reduce((sum, d) => sum + d?.totalMiles, 0)?.toLocaleString()}
            </div>
            <div className="text-xs text-muted-foreground">Total Miles</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverLeaderboard;